import { useEffect, useState } from 'react'
import { Download, Search, X } from 'lucide-react'
import { api, downloadCsv, errorMessage, type AdminOrderRow, type AdminOrderItem, type AdminEvent } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { fmtMoney, fmtRelative, fmtTime } from '@/lib/format'
import { useI18n, type TKey } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

const STATUS_TONES: Record<string, 'success'|'info'|'warning'|'danger'|'default'> = {
  paid: 'info', fulfilled: 'success', pending: 'warning', refunded: 'default', cancelled: 'danger',
}

export function OrdersScreen() {
  const { t } = useI18n()
  const toast = useToast()
  const [orders, setOrders] = useState<AdminOrderRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [drillId, setDrillId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  function load() {
    setBusy(true); setError(null)
    api.listOrders({ q: q || undefined, status: status || undefined, limit: 100 })
      .then((r) => { setOrders(r.orders); setTotal(r.total) })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }

  useEffect(() => { load() }, [])

  async function setOrderStatus(o: AdminOrderRow, next: AdminOrderRow['status']) {
    try {
      await api.patchOrder(o.id, { status: next })
      setOrders((prev) => prev?.map((x) => (x.id === o.id ? { ...x, status: next } : x)) ?? null)
      const fromLabel = t(`status.${o.status}` as TKey)
      const toLabel = t(`status.${next}` as TKey)
      toast.success(`${t('orders.col.order')} ${o.id.slice(-8)}`, t('orders.toast.statusChanged', { from: fromLabel, to: toLabel }))
    } catch (e) {
      toast.error(t('orders.toast.updateFailed'), errorMessage(e))
    }
  }

  async function exportCsv() {
    setExporting(true)
    try {
      await downloadCsv('/admin/orders/export.csv', 'orders.csv')
      toast.success(t('action.export'), t('orders.toast.exportReady'))
    } catch (e) {
      toast.error(t('orders.toast.exportFailed'), errorMessage(e))
    } finally {
      setExporting(false)
    }
  }

  if (error) return <PageError msg={error}/>
  if (!orders && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('orders.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('orders.subtitle', { n: total.toLocaleString() })}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={exporting}>
          <Download size={14} strokeWidth={1.75}/>
          {exporting ? t('action.exporting') : t('action.export')}
        </Button>
      </header>

      <form
        className="flex gap-2 items-center"
        onSubmit={(e) => { e.preventDefault(); load() }}
      >
        <div className="relative max-w-sm flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60"/>
          <Input
            placeholder={t('orders.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-border bg-card px-3 text-sm"
        >
          <option value="">{t('users.filter.all')}</option>
          <option value="pending">{t('status.pending')}</option>
          <option value="paid">{t('status.paid')}</option>
          <option value="fulfilled">{t('status.fulfilled')}</option>
          <option value="refunded">{t('status.refunded')}</option>
          <option value="cancelled">{t('status.cancelled')}</option>
        </select>
        <Button type="submit" disabled={busy}>{t('action.search')}</Button>
      </form>

      <Table>
        <THead>
          <Tr>
            <Th>{t('orders.col.order')}</Th>
            <Th>{t('orders.col.user')}</Th>
            <Th>{t('orders.col.amount')}</Th>
            <Th>{t('orders.col.status')}</Th>
            <Th>{t('orders.col.provider')}</Th>
            <Th>{t('orders.col.created')}</Th>
            <Th className="text-right pr-4">{t('orders.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {orders?.map((o) => (
            <Tr
              key={o.id}
              className="cursor-pointer"
              onClick={() => setDrillId(o.id)}
            >
              <Td className="font-mono text-xs">{o.id.slice(-12)}</Td>
              <Td>{o.userEmail ?? <span className="text-muted-foreground">—</span>}</Td>
              <Td className="tabular-nums">{fmtMoney(o.amountCents, o.currency)}</Td>
              <Td><Badge tone={STATUS_TONES[o.status] ?? 'default'}>{t(`status.${o.status}` as TKey)}</Badge></Td>
              <Td className="text-muted-foreground">{o.paymentProvider}</Td>
              <Td className="text-muted-foreground">{fmtRelative(o.createdAt)}</Td>
              <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                {o.status === 'pending' && (
                  <Button size="sm" variant="outline" onClick={() => setOrderStatus(o, 'cancelled')}>{t('action.cancel')}</Button>
                )}
                {(o.status === 'paid' || o.status === 'fulfilled') && (
                  <Button size="sm" variant="outline" onClick={() => setOrderStatus(o, 'refunded')}>{t('action.refund')}</Button>
                )}
              </Td>
            </Tr>
          ))}
          {orders?.length === 0 && (
            <Tr>
              <Td className="text-center py-8 text-muted-foreground">{t('orders.empty')}</Td>
            </Tr>
          )}
        </TBody>
      </Table>

      {drillId && (
        <OrderDrillIn
          orderId={drillId}
          onClose={() => setDrillId(null)}
          onChanged={(next) => {
            setOrders((prev) => prev?.map((x) => (x.id === drillId ? { ...x, status: next } : x)) ?? null)
          }}
        />
      )}
    </div>
  )
}

function OrderDrillIn({ orderId, onClose, onChanged }: {
  orderId: string
  onClose: () => void
  onChanged: (status: AdminOrderRow['status']) => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const [data, setData] = useState<{ order: AdminOrderRow; items: AdminOrderItem[] } | null>(null)
  const [history, setHistory] = useState<AdminEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // null = closed; non-null = ship dialog mounted
  const [fulfillOpen, setFulfillOpen] = useState(false)

  async function reload() {
    try {
      const [o, ev] = await Promise.all([api.getOrder(orderId), api.listEvents({ limit: 100 })])
      setData({ order: o.order as any, items: o.items })
      setHistory(ev.events.filter((e) => {
        const p = e.payload?.data ?? e.payload ?? {}
        return p?.id === orderId || p?.orderId === orderId
      }))
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  useEffect(() => {
    setData(null); setHistory(null); setError(null)
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  async function changeStatus(next: AdminOrderRow['status']) {
    setBusy(true)
    try {
      await api.patchOrder(orderId, { status: next })
      toast.success(`${t('orders.col.order')} ${orderId.slice(-8)}`, `→ ${t(`status.${next}` as TKey)}`)
      onChanged(next)
      await reload()
    } catch (e) {
      toast.error(t('orders.toast.updateFailed'), errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  // 发货：paid → fulfilled，可附带凭证文本（成品号类）或留空（代充类）。
  // 走同一个 PATCH，note 由后端写到 metadata.fulfillment_note，并触发"已发货"邮件。
  async function submitFulfill(note: string) {
    setBusy(true)
    try {
      await api.patchOrder(orderId, { status: 'fulfilled', note: note || undefined })
      toast.success(`${t('orders.col.order')} ${orderId.slice(-8)}`, t('orders.fulfill.toastSent'))
      onChanged('fulfilled')
      setFulfillOpen(false)
      await reload()
    } catch (e) {
      toast.error(t('orders.toast.updateFailed'), errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <aside className="w-full max-w-lg bg-card border-l border-border h-full overflow-auto animate-[slideUp_0.18s_ease-out]">
        <header className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-border bg-card/95 backdrop-blur">
          <div>
            <div className="text-xs text-muted-foreground">{t('orders.col.order')}</div>
            <div className="font-mono text-sm">{orderId.slice(-12)}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X size={18}/>
          </button>
        </header>

        {error && <div className="p-4"><div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{error}</div></div>}
        {!data && !error && <div className="p-6 text-sm text-muted-foreground">{t('app.loading')}</div>}

        {data && (
          <div className="p-4 space-y-5">
            <section className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t('orders.col.status')}>
                <Badge tone={STATUS_TONES[data.order.status] ?? 'default'}>{t(`status.${data.order.status}` as TKey)}</Badge>
              </Field>
              <Field label={t('orders.col.amount')}>
                <span className="font-medium tabular-nums">{fmtMoney(data.order.amountCents, data.order.currency)}</span>
              </Field>
              <Field label={t('orders.col.user')}>{data.order.userEmail ?? '—'}</Field>
              <Field label={t('orders.col.provider')}>{data.order.paymentProvider}</Field>
              <Field label={t('orders.col.created')}>{fmtTime(data.order.createdAt)}</Field>
              <Field label={t('status.paid')}>{data.order.paidAt ? fmtTime(data.order.paidAt) : '—'}</Field>
            </section>

            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Items</h3>
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-accent/30">
                    <tr>
                      <th className="text-left table-cell">{t('products.col.name')}</th>
                      <th className="text-right table-cell">Qty</th>
                      <th className="text-right table-cell pr-3">Unit</th>
                      <th className="text-right table-cell pr-3">Line</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((it) => (
                      <tr key={it.id}>
                        <td className="table-cell font-mono text-xs">{it.productId.slice(-12)}</td>
                        <td className="table-cell text-right tabular-nums">{it.quantity}</td>
                        <td className="table-cell text-right tabular-nums pr-3">{fmtMoney(it.unitPriceCents, data.order.currency)}</td>
                        <td className="table-cell text-right tabular-nums pr-3">{fmtMoney(it.unitPriceCents * it.quantity, data.order.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="flex flex-wrap gap-2">
              {data.order.status === 'pending' && (
                <>
                  <Button onClick={() => changeStatus('paid')} disabled={busy}>{t('status.paid')}</Button>
                  <Button variant="outline" onClick={() => changeStatus('cancelled')} disabled={busy}>{t('action.cancel')}</Button>
                </>
              )}
              {data.order.status === 'paid' && (
                <>
                  <Button onClick={() => setFulfillOpen(true)} disabled={busy}>{t('orders.fulfill.cta')}</Button>
                  <Button variant="outline" onClick={() => changeStatus('refunded')} disabled={busy}>{t('action.refund')}</Button>
                </>
              )}
              {data.order.status === 'fulfilled' && (
                <Button variant="outline" onClick={() => changeStatus('refunded')} disabled={busy}>{t('action.refund')}</Button>
              )}
            </section>

            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">History</h3>
              {!history && <div className="text-sm text-muted-foreground">{t('app.loading')}</div>}
              {history && history.length === 0 && (
                <div className="text-sm text-muted-foreground">{t('stats.recent.empty')}</div>
              )}
              {history && history.length > 0 && (
                <ol className="relative border-l border-border ml-1 space-y-3 pl-4">
                  {history.map((e) => (
                    <li key={e.id} className="relative">
                      <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full bg-primary"/>
                      <div className="text-sm">{eventLabel(e)}</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtTime(e.createdAt)}{e.payload?.actor ? <> · {e.payload.actor}</> : null}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )}
      </aside>

      {fulfillOpen && data && (
        <FulfillDialog
          orderShortCode={orderId.slice(-8)}
          busy={busy}
          onCancel={() => setFulfillOpen(false)}
          onSubmit={(note) => void submitFulfill(note)}
        />
      )}
    </div>
  )
}

// Dialog shown when admin marks 已支付 → 已发货. Two cases collapsed into one
// form: paste credentials (成品号 etc.) OR submit empty (代充 / 服务类). The
// API accepts an optional `note` either way; the user sees the text via the
// 「查看宝贝」 button on /orders.
// 与后端 patchSchema.note.max() 保持一致 —— 任何一边改动另一边也要跟进。
const FULFILL_NOTE_MAX = 5000

function FulfillDialog({ orderShortCode, busy, onCancel, onSubmit }: {
  orderShortCode: string
  busy: boolean
  onCancel: () => void
  onSubmit: (note: string) => void
}) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const trimmed = text.trim()
  const hasContent = trimmed.length > 0
  const len = trimmed.length
  const overLimit = len > FULFILL_NOTE_MAX
  const nearLimit = len > FULFILL_NOTE_MAX * 0.9 && !overLimit

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-2xl">
        <header className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold">{t('orders.fulfill.title')}</h3>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">#{orderShortCode}</div>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18}/>
          </button>
        </header>
        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">{t('orders.fulfill.body')}</p>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('orders.fulfill.placeholder')}
            rows={8}
            disabled={busy}
            className={
              'w-full rounded-md border bg-background px-3 py-2 text-sm font-mono leading-relaxed resize-y min-h-[180px] focus:outline-none focus:ring-2 disabled:opacity-50 ' +
              (overLimit ? 'border-danger focus:ring-danger/40' : 'border-border focus:ring-ring')
            }
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-muted-foreground">{t('orders.fulfill.hint')}</p>
            <span className={
              'text-[11px] font-mono tabular-nums ' +
              (overLimit ? 'text-danger' : nearLimit ? 'text-warning' : 'text-muted-foreground')
            }>
              {len}/{FULFILL_NOTE_MAX}
            </span>
          </div>
        </div>
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onCancel} disabled={busy}>{t('orders.fulfill.cancel')}</Button>
          <Button onClick={() => onSubmit(trimmed)} disabled={busy || overLimit}>
            {hasContent ? t('orders.fulfill.submitWith') : t('orders.fulfill.submitNone')}
          </Button>
        </footer>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground/70 mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  )
}

function eventLabel(e: AdminEvent): string {
  const p = e.payload?.data ?? e.payload ?? {}
  switch (e.type) {
    case 'order.create':         return `Order placed · $${(p.amountCents / 100).toFixed(2)} ${p.currency ?? ''}`
    case 'admin.order.update':   return `Status: ${p.from} → ${p.to}${p.note ? ` · "${p.note}"` : ''}`
    case 'referral.award':       return `Referral credit · $${(p.rewardCents / 100).toFixed(2)}`
    default:                     return e.type
  }
}
